"use client";

import { Field, Icon } from "@plura/client/components";
import type { IconName } from "@plura/shared/icon-glyphs";
import { cn } from "@plura/shared/utils";
import { Button } from "../../ui/Button";
import { FormEvent, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";

interface HelpHeroProps {
  /** Quick topic pills rendered under the search. */
  topics?: { label: string; href: string; icon: IconName }[];
}

const HelpHero = ({ topics = [] }: HelpHeroProps) => {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/help/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  /** Input sits inside the bar: strip browser outline; outer shell uses theme focus (focus-within). */
  const searchFieldInputClass = cn(
    "!h-14 min-h-0 w-full !rounded-none !border-0 !bg-transparent !px-3 !py-0 !shadow-none",
    "font-body text-base leading-normal text-dark placeholder:text-[#555555]/50",
    "!outline-none !outline-offset-0 [outline-style:none]",
    "ring-0 ring-offset-0 focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0",
    "hover:!border-transparent focus:!border-transparent",
  );

  return (
    <div className="bg-canvas mx-auto w-screen max-w-[2200px] overflow-hidden pt-32 max-md:pt-[110px]">
      <div className="relative mx-auto flex w-[920px] max-w-full flex-col items-center gap-5 max-md:px-5">
        <div className="relative z-[2] flex w-full flex-col items-center justify-center gap-5 text-center max-md:gap-4">
          <h1 className="text-h1 font-heading m-0 font-medium text-dark">How can we help you?</h1>
          <p className="text-description m-0">
            Search our documentation and articles to find answers to your questions
          </p>
          <form onSubmit={handleSearch} className="mt-5 w-full max-w-[700px]">
            <div
              className={cn(
                "flex h-[4.25rem] min-h-[4.25rem] items-center gap-0 rounded-full border border-[#e5e5e5] bg-white p-1.5",
                "transition-[border-color] duration-200",
                "focus-within:border-green focus-within:outline-none",
              )}
            >
              <Icon
                name="search"
                className="ml-2 mr-1 size-6 shrink-0 fill-dark text-dark max-md:ml-1.5 max-md:size-5 [&_path]:fill-current"
                aria-hidden
              />
              <Field
                type="text"
                name="q"
                autoComplete="off"
                aria-label="Search help topics"
                placeholder="Search for help topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cn(
                  "min-w-0 flex-1",
                  "[&_input]:!outline-none [&_input]:!outline-offset-0 [&_input:focus]:!outline-none [&_input:focus-visible]:!outline-none",
                )}
                classInput={searchFieldInputClass}
              />
              <Button type="submit" className="shrink-0 max-md:!px-5 max-md:!text-sm">
                Search
              </Button>
            </div>
          </form>

          {topics.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              {topics.map((topic) => (
                <Link
                  key={topic.href}
                  href={topic.href}
                  className="font-body inline-flex h-9 items-center gap-2 rounded-full border border-[#e5e5e5] bg-white pl-3.5 pr-4 text-sm font-medium text-dark transition-colors duration-200 hover:border-dark hover:bg-shade"
                >
                  <Icon name={topic.icon} className="!size-4 shrink-0 fill-current text-[#555555] [&_path]:fill-current" aria-hidden />
                  {topic.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpHero;
